import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';

interface TruncatedIdProps {
    id: string | number | null | undefined;
    headChars?: number;
    tailChars?: number;
    /** Show the full id without truncation if its length is less than or equal to this value. */
    minLengthToTruncate?: number;
}

/**
 * Renders a compact transaction ID (first N + '…' + last M chars) with a copy button.
 * Clicking the copy button stops event propagation so it doesn't trigger a parent row click.
 */
export const TruncatedId: React.FC<TruncatedIdProps> = ({
    id,
    headChars = 8,
    tailChars = 6,
    minLengthToTruncate = 16,
}) => {
    const [copied, setCopied] = useState(false);

    if (id === null || id === undefined || id === '') {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }

    const fullId = String(id);
    const shouldTruncate = fullId.length > minLengthToTruncate;
    const display = shouldTruncate
        ? `${fullId.slice(0, headChars)}…${fullId.slice(-tailChars)}`
        : fullId;

    const handleCopy = async (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(fullId);
            } else {
                // Fallback for older browsers / non-HTTPS dev contexts
                const textarea = document.createElement('textarea');
                textarea.value = fullId;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error('[TruncatedId] Copy failed:', err);
        }
    };

    const tooltipTitle = copied ? 'Copied!' : fullId;

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
            <Tooltip title={tooltipTitle} arrow placement="top">
                <Typography
                    variant="body2"
                    component="span"
                    sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: '0.8125rem',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {display}
                </Typography>
            </Tooltip>
            <IconButton
                size="small"
                onClick={handleCopy}
                aria-label="copy id"
                sx={{ p: 0.25, ml: 0.25 }}
            >
                {copied ? (
                    <Check fontSize="inherit" sx={{ fontSize: 14, color: 'success.main' }} />
                ) : (
                    <ContentCopy fontSize="inherit" sx={{ fontSize: 14 }} />
                )}
            </IconButton>
        </Box>
    );
};

export default TruncatedId;
